import { zodResolver } from "@hookform/resolvers/zod";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { t } from "i18next";
import { X } from "lucide-react";
import type { JSX } from "react";
import { useEffect, useState } from "react";
import {
  type ControllerRenderProps,
  type DefaultValues,
  type FieldValues,
  type Path,
  useForm,
} from "react-hook-form";
import type { ZodType } from "zod";
import { AppConfig } from "@/common/appConfig";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { FormField } from "@/components/ui/form/FormField";
import { V3Button, V3IconButton } from "@/components/ui/v3/button";

interface SingleFormDialogProps<T extends FieldValues> {
  title: string;
  // The field's name ("Phone"): the v3.2 sheet's title.
  label?: string;
  schema: ZodType<T, T>;
  defaultValues?: DefaultValues<T>;
  name: Path<T>;
  trigger: React.ReactNode;
  onSubmit: (data: T) => void;
  render: (field: ControllerRenderProps<T, Path<T>>) => JSX.Element;
  // Defaults to "Save".
  submitLabel?: string;
}

export const SingleFormDialog = <T extends FieldValues>(
  props: SingleFormDialogProps<T>,
): JSX.Element => {
  const [open, setOpen] = useState(false);

  const form = useForm<T>({
    resolver: zodResolver(props.schema),
    defaultValues: props.defaultValues,
  });

  useEffect(() => {
    if (open) {
      form.reset(props.defaultValues);
    }
  }, [open, props.defaultValues, form]);

  const handleClose = (): void => {
    setOpen(false);
  };

  const handleSubmit = (data: T): void => {
    props.onSubmit(data);
    handleClose();
  };

  if (AppConfig.enableAppRedesign)
    return (
      <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
        <DialogPrimitive.Trigger asChild>
          {props.trigger}
        </DialogPrimitive.Trigger>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/30 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0" />
          <DialogPrimitive.Content
            aria-describedby={undefined}
            className="v3-remap fixed z-50 flex flex-col gap-3 bg-v3-panel font-v3 text-v3-text shadow-v3-pop outline-hidden max-sm:inset-x-0 max-sm:bottom-0 max-sm:max-h-[92dvh] max-sm:overflow-y-auto max-sm:rounded-t-[22px] max-sm:px-5 max-sm:pt-3 max-sm:pb-[max(1.25rem,env(safe-area-inset-bottom))] max-sm:data-[state=open]:animate-in max-sm:data-[state=open]:slide-in-from-bottom-8 sm:left-1/2 sm:top-1/2 sm:w-[420px] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-[18px] sm:p-6 sm:data-[state=open]:animate-in sm:data-[state=open]:zoom-in-95 sm:data-[state=open]:fade-in-0"
            data-testid="settings-field-sheet"
          >
            <div
              aria-hidden
              className="mx-auto h-1 w-9 rounded-full bg-v3-surface-3 sm:hidden"
            />
            <div className="flex items-center gap-2">
              <DialogPrimitive.Title className="min-w-0 flex-1 text-[17px] font-semibold leading-6">
                {props.label ?? props.title}
              </DialogPrimitive.Title>
              <DialogPrimitive.Close asChild>
                <V3IconButton className="-mr-2" label={t("common.close")}>
                  <X />
                </V3IconButton>
              </DialogPrimitive.Close>
            </div>
            <Form {...form}>
              <form
                className="flex flex-col gap-4"
                onSubmit={form.handleSubmit(handleSubmit)}
              >
                <FormField
                  control={form.control}
                  name={props.name}
                  render={({ field }) => props.render(field)}
                />
                <div className="grid grid-cols-2 gap-2 sm:flex sm:justify-end">
                  <DialogPrimitive.Close asChild>
                    <V3Button className="max-sm:h-12" variant="secondary">
                      {t("settings.profile.cancelModal")}
                    </V3Button>
                  </DialogPrimitive.Close>
                  <V3Button
                    className="max-sm:h-12"
                    data-testid="settings-field-save"
                    type="submit"
                    variant="primary"
                  >
                    {props.submitLabel ?? t("settings.profile.saveModal")}
                  </V3Button>
                </div>
              </form>
            </Form>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{props.trigger}</DialogTrigger>
      <DialogDescription className="sr-only">{props.name}</DialogDescription>
      <DialogContent aria-describedby={props.name}>
        <DialogHeader>
          <DialogTitle>{props.title}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            className="space-y-4"
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <FormField
              control={form.control}
              name={props.name}
              render={({ field }) => props.render(field)}
            />
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">
                  {t("settings.profile.cancelModal")}
                </Button>
              </DialogClose>
              <Button type="submit" variant="default">
                {props.submitLabel ?? t("settings.profile.saveModal")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
